import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EXPERIENCE_OPTIONS = [
  { value: "under_year", label: "До года" },
  { value: "one_to_three_years", label: "1–3 года" },
  { value: "over_three_years", label: "Больше 3 лет" },
];
const WORK_OPTIONS = [
  { value: "hobby", label: "Хобби" },
  { value: "side_income", label: "Дополнительный заработок" },
  { value: "main_work", label: "Основная работа" },
];
const ORDERS_OPTIONS = [
  { value: "zero_to_five", label: "0–5" },
  { value: "six_to_fifteen", label: "6–15" },
  { value: "over_fifteen", label: "Больше 15" },
];
const ORDER_METHOD_OPTIONS = [
  { value: "notebook", label: "Блокнот или ежедневник" },
  { value: "phone_notes", label: "Заметки в телефоне" },
  { value: "messengers", label: "Мессенджеры" },
  { value: "spreadsheets", label: "Таблицы" },
  { value: "crm", label: "Специальный сервис или CRM" },
  { value: "not_systematic", label: "Никак системно" },
  { value: "other", label: "Другое" },
];
const COSTING_OPTIONS = [
  { value: "exact", label: "Всегда считаю точно" },
  { value: "not_every_order", label: "Считаю не для каждого заказа" },
  { value: "approximate", label: "Считаю приблизительно" },
  { value: "usually_not", label: "Обычно не считаю" },
];
const RECIPE_OPTIONS = [
  { value: "paper", label: "Бумажные записи" },
  { value: "notes", label: "Заметки" },
  { value: "documents", label: "PDF или документы" },
  { value: "messengers", label: "Мессенджеры" },
  { value: "spreadsheets", label: "Таблицы" },
  { value: "apps", label: "Специальные приложения или сервисы" },
  { value: "other", label: "Другое" },
];
const DEVICE_OPTIONS = [
  { value: "phone", label: "Телефон" },
  { value: "computer", label: "Компьютер или ноутбук" },
  { value: "tablet", label: "Планшет" },
  { value: "multiple", label: "Несколько устройств" },
];
const BRAND_STYLE_OPTIONS = [
  { value: "established", label: "Да, у меня уже есть фирменный стиль" },
  { value: "elements_no_system", label: "Есть отдельные элементы, но единого стиля нет" },
  { value: "want_style", label: "Пока нет, но хотелось бы оформить" },
  { value: "not_needed", label: "Мне это сейчас не нужно" },
];
const BRAND_ASSET_OPTIONS = [
  { value: "logo", label: "Логотип" }, { value: "business_cards", label: "Визитки" },
  { value: "stickers", label: "Наклейки" }, { value: "tags", label: "Бирки" },
  { value: "ribbons", label: "Ленты" }, { value: "packaging", label: "Упаковку" },
  { value: "social_media", label: "Оформление социальных сетей" }, { value: "other", label: "Другое" },
];

const EMPTY_FORM = {
  social_url: "",
  craft_experience: "",
  work_mode: "",
  orders_per_month: "",
  order_management_methods: [],
  costing_method: "",
  recipe_storage_methods: [],
  planned_device: "",
  desired_help_text: "",
  brand_style_state: "",
  desired_brand_assets: [],
};

const DEFAULT_PRE_CONTENT = [
  { key: "social_url", text: "Ссылка на твою рабочую страницу / соцсеть", kind: "text", required: true },
  { key: "craft_experience", text: "Как давно ты занимаешься кондитерским делом?", kind: "radio", options: EXPERIENCE_OPTIONS, required: true },
  { key: "work_mode", text: "Как сейчас выглядит твоя работа с заказами?", kind: "radio", options: WORK_OPTIONS, required: true },
  { key: "orders_per_month", text: "Сколько заказов у тебя обычно бывает в месяц?", kind: "radio", options: ORDERS_OPTIONS, required: true },
  { key: "order_management_methods", text: "Как ты сейчас ведёшь заказы?", kind: "checkbox", options: ORDER_METHOD_OPTIONS, required: true },
  { key: "costing_method", text: "Как ты обычно считаешь себестоимость изделий?", kind: "radio", options: COSTING_OPTIONS, required: true },
  { key: "recipe_storage_methods", text: "Где ты хранишь свои рецепты?", kind: "checkbox", options: RECIPE_OPTIONS, required: true },
  { key: "planned_device", text: "На каком устройстве ты планируешь работать с Мастером заказа во время знакомства?", kind: "radio", options: DEVICE_OPTIONS, required: true },
  { key: "desired_help_text", text: "Что в работе с заказами тебе хотелось бы упростить в первую очередь?", kind: "textarea", required: true },
  { key: "brand_style_state", text: "Есть ли у тебя оформление для своего кондитерского бренда?", kind: "radio", options: BRAND_STYLE_OPTIONS, required: true },
  { key: "desired_brand_assets", text: "Что тебе хотелось бы оформить для своего бренда?", kind: "checkbox", options: BRAND_ASSET_OPTIONS, required: false },
];
const TEST_IDS = {
  social_url: "mo-testing-social-input", craft_experience: "mo-testing-experience",
  work_mode: "mo-testing-work-mode", orders_per_month: "mo-testing-orders-per-month",
  order_management_methods: "mo-testing-order-methods", costing_method: "mo-testing-costing",
  recipe_storage_methods: "mo-testing-recipe-storage", planned_device: "mo-testing-device",
  desired_help_text: "mo-testing-desired-help", brand_style_state: "mo-testing-brand-state",
  desired_brand_assets: "mo-testing-brand-assets",
};

function EyebrowTitle({ title }) {
  return (
    <header className="mb-8">
      <div className="text-[10.5px] tracking-[0.3em] uppercase mb-3" style={{ color: "#8B6F4B" }}>
        ANOKINA STUDIO · МАСТЕР ЗАКАЗА
      </div>
      <h1 className="leading-[1.1] font-serif" style={{ color: "#2A1D14", fontSize: "clamp(28px, 4.6vw, 40px)" }}>
        {title}
      </h1>
    </header>
  );
}

function PageFrame({ children, preview = false }) {
  const nav = useNavigate();
  return (
    <main className="min-h-screen" style={{ background: "#EFE6D5" }} data-testid="mo-testing-page">
      <div className="mx-auto px-5 sm:px-10 py-12 sm:py-16" style={{ maxWidth: 880 }}>
        <button type="button" onClick={() => nav(preview ? "/admin?tab=mo_testing" : "/hall")} data-testid="mo-testing-back-top"
          className="mb-8 inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase h-9 px-2 rounded-xl"
          style={{ color: "#5B4326" }}>
          {preview ? "← Вернуться в панель управления" : "← Назад"}
        </button>
        {children}
      </div>
    </main>
  );
}

function StatusChip({ children }) {
  return (
    <div className="inline-flex items-center rounded-full px-3.5 py-1.5 text-[11.5px] tracking-[0.14em] uppercase mb-6"
      style={{ background: "#F1E4CC", color: "#5B4326" }}>
      {children}
    </div>
  );
}

function Button({ children, onClick, testid, secondary = false, disabled = false, type = "button" }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} data-testid={testid}
      className="h-11 px-6 rounded-xl text-[13px] disabled:opacity-60"
      style={secondary
        ? { background: "transparent", color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }
        : { background: "#4A3728", color: "#F7EFE0" }}>
      {children}
    </button>
  );
}

function StatePage({ testid, title, status, children, actions }) {
  return (
    <section data-testid={testid}>
      <EyebrowTitle title={title} />
      {status && <StatusChip>{status}</StatusChip>}
      <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>{children}</div>
      <div className="mt-10 flex flex-wrap gap-3">{actions}</div>
    </section>
  );
}

function AcceptedState({ preview = false }) {
  const nav = useNavigate();
  const [starting, setStarting] = useState(false);
  const startTrial = async () => {
    if (starting) return;
    if (preview) {
      toast.success("Режим QA: таймер и заявка не изменены");
      return;
    }
    setStarting(true);
    try {
      await api.post("/master-order-testing/start-access");
      nav("/master-order");
    } catch (error) {
      toast.error(formatApiError(error) || "Не удалось начать знакомство");
    } finally {
      setStarting(false);
    }
  };
  return (
    <StatePage testid="mo-testing-accepted-state" title="Твоя заявка принята" status="Можно начинать"
      actions={<>
        <Button testid="mo-testing-start-button" onClick={startTrial} disabled={starting}>
          {starting ? "Открываем…" : "Начать"}
        </Button>
        <Button secondary onClick={() => nav(preview ? "/admin?tab=mo_testing" : "/hall")}>
          {preview ? "← Вернуться в панель управления" : "Вернуться в Anokina Studio"}
        </Button>
      </>}>
      <div className="space-y-3">
        <p>Открой Мастер заказа и попробуй использовать его так, как обычно работаешь.</p>
        <p>Не старайся проверить все функции — начни с того, что сейчас нужно в работе.</p>
        <p>Если что-то непонятно, коротко зафиксируй, на каком шаге возник вопрос.</p>
        <p>У тебя есть 24 часа с момента, когда ты нажмёшь «Начать» — таймер стартует именно тогда, не раньше.</p>
      </div>
    </StatePage>
  );
}

function ActiveState({ application, preview = false }) {
  const nav = useNavigate();
  const expires = application?.access_expires_at || application?.test_access_expires_at;
  const formatted = expires ? new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit",
  }).format(new Date(expires)) : "";
  return (
    <StatePage testid="mo-testing-active-state" title="Знакомство уже началось" status="Доступ открыт"
      actions={<Button testid="mo-testing-open-master-order" onClick={() => nav(preview ? "/admin?tab=mo_testing" : "/master-order")}>
        {preview ? "← Вернуться в панель управления" : "Продолжить работу"}
      </Button>}>
      <p>Твой доступ к Мастеру заказа активен.</p>
      {formatted && <p className="mt-2 text-[13.5px]">Доступ до {formatted}</p>}
    </StatePage>
  );
}

function CompletedState({ feedbackReceived = false, preview = false }) {
  const nav = useNavigate();
  return (
    <StatePage testid={feedbackReceived ? "mo-testing-feedback-received-state" : "mo-testing-completed-state"}
      title={feedbackReceived ? "Спасибо за обратную связь" : "Знакомство завершилось"}
      status={feedbackReceived ? "Отзыв получен" : "24 часа завершились"}
      actions={<>
        {!feedbackReceived && <Button testid="mo-testing-share-feedback" onClick={() => nav(preview ? "/master-order-testing/feedback?preview_state=feedback_form" : "/master-order-testing/feedback")}>Поделиться впечатлениями</Button>}
        <Button secondary={ !feedbackReceived } onClick={() => nav(preview ? "/admin?tab=mo_testing" : "/hall")}>
          {preview ? "← Вернуться в панель управления" : "Вернуться в Anokina Studio"}
        </Button>
      </>}>
      {feedbackReceived ? (
        <p>Твои ответы сохранены. Спасибо — твоя обратная связь помогает сделать Мастер заказа понятнее для реальной работы.</p>
      ) : (
        <>
          <p>Твои 24 часа в Мастере заказа завершились.</p>
          <p className="mt-3">Все созданные материалы сохранены у тебя в Рабочей тетради.</p>
        </>
      )}
    </StatePage>
  );
}

function ApplicationForm({ user, existing, preview, content, onSaved, onCancel }) {
  const [form, setForm] = useState(() => existing ? {
    ...EMPTY_FORM,
    ...Object.fromEntries(Object.keys(EMPTY_FORM).map((key) => [key, existing[key] ?? EMPTY_FORM[key]])),
  } : EMPTY_FORM);
  const [consent, setConsent] = useState(!!existing?.consent_accepted);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);
  const set = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => current[key] ? { ...current, [key]: undefined } : current);
  };
  const toggle = (key, value) => set(key, form[key].includes(value)
    ? form[key].filter((item) => item !== value)
    : [...form[key], value]);

  const validate = () => {
    const next = {};
    for (const question of content) {
      const value = form[question.key];
      const missing = Array.isArray(value)
        ? !value.length
        : typeof value === "string" ? !value.trim() : !value;
      if (question.required && missing) {
        next[question.key] = question.key === "social_url"
          ? "Укажи ссылку на рабочую страницу или соцсеть."
          : question.kind === "checkbox" ? "Выбери хотя бы один вариант." : "Заполни это поле.";
      }
    }
    if (["elements_no_system", "want_style"].includes(form.brand_style_state) && !form.desired_brand_assets.length) {
      next.desired_brand_assets = "Выбери хотя бы один вариант.";
    }
    if (!consent) next.consent = "Необходимо согласие на обработку данных.";
    setErrors(next);
    const firstInvalid = Object.keys(next)[0];
    if (firstInvalid) requestAnimationFrame(() => {
      const target = formRef.current?.querySelector(`[data-validation-key="${firstInvalid}"]`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusTarget = target?.matches("input, textarea, button")
        ? target : target?.querySelector("input, textarea, button");
      focusTarget?.focus({ preventScroll: true });
    });
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    if (preview) {
      toast.success("Проверка прошла — данные не сохранены");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        social_url: form.social_url.trim(),
        desired_help_text: form.desired_help_text.trim(),
        desired_brand_assets: ["elements_no_system", "want_style"].includes(form.brand_style_state)
          ? form.desired_brand_assets : [],
        consent_accepted: true,
      };
      const response = existing
        ? await api.patch("/master-order-testing/applications/mine", payload)
        : await api.post("/master-order-testing/applications", payload);
      onSaved(response.data, !existing);
      toast.success(existing ? "Заявка обновлена" : "Заявка отправлена");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.application) onSaved(detail.application, false);
      else toast.error(formatApiError(error) || "Не удалось сохранить заявку");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={submit} className="mt-12 max-w-[62ch] space-y-8" data-testid="mo-testing-form" noValidate>
      <ReadOnlyField label="Имя" value={user?.display_name || user?.name || "—"} testid="mo-testing-name-prefill" />
      {content.map((question) => {
        if (question.key === "desired_brand_assets" && !["elements_no_system", "want_style"].includes(form.brand_style_state)) return null;
        const common = { key: question.key, validationKey: question.key, label: question.text, value: form[question.key], error: errors[question.key], testid: TEST_IDS[question.key] };
        if (question.kind === "radio") return <RadioField {...common} options={question.options} onChange={(value) => set(question.key, value)} />;
        if (question.kind === "checkbox") return <CheckboxField {...common} options={question.options} onToggle={(value) => toggle(question.key, value)} />;
        if (question.kind === "textarea") return <TextAreaField {...common} onChange={(value) => set(question.key, value)} />;
        return <TextField {...common} onChange={(value) => set(question.key, value)} />;
      })}
      <div data-validation-key="consent">
      <label className="flex items-start gap-3 cursor-pointer text-[14px] leading-[1.55]" style={{ color: "#3E2E20" }}>
        <input type="checkbox" checked={consent} onChange={(event) => {
          setConsent(event.target.checked);
          setErrors((current) => ({ ...current, consent: undefined }));
        }}
          className="mt-1 h-4 w-4 accent-[#4A3728]" data-testid="mo-testing-consent-checkbox" />
        <span>Я даю согласие на обработку данных, указанных в анкете, для участия в 24-часовом знакомстве с Мастером заказа.</span>
      </label>
      {errors.consent && <ErrorText>{errors.consent}</ErrorText>}
      </div>
      <div className="flex flex-wrap gap-3">
        <Button type="submit" testid="mo-testing-submit-button" disabled={saving}>
          {saving ? "Сохраняем…" : existing ? "Сохранить изменения" : "Проверить заполнение"}
        </Button>
        {existing && <Button secondary onClick={onCancel}>Отмена</Button>}
      </div>
    </form>
  );
}

export default function MasterOrderTesting() {
  const nav = useNavigate();
  const { user, initializing } = useAuth();
  const [search] = useSearchParams();
  const previewState = search.get("preview_state");
  const preview = !!previewState && ["owner", "administrator"].includes(user?.role);
  const [content, setContent] = useState({ pre_trial: DEFAULT_PRE_CONTENT, copy: {} });
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    document.title = "24-часовое знакомство с Мастером заказа — Anokina Studio";
  }, []);

  useEffect(() => {
    if (initializing) return;
    if (!user) {
      setLoading(false);
      return;
    }
    if (preview) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [access, application, configuredContent] = await Promise.all([
          api.get("/master-order/access-mode"),
          api.get("/master-order-testing/applications/mine"),
          api.get("/master-order-testing/content"),
        ]);
        if (cancelled) return;
        if (access?.data?.mode === "internal") {
          nav("/master-order", { replace: true });
          return;
        }
        setExisting(application.data || null);
        if (configuredContent.data) setContent(configuredContent.data);
      } catch (error) {
        if (!cancelled) toast.error(formatApiError(error) || "Не удалось загрузить состояние заявки");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initializing, user, preview, nav]);

  useEffect(() => {
    if (!preview) return;
    api.get("/master-order-testing/content").then(({ data }) => setContent(data)).catch(() => {});
  }, [preview]);

  const saved = (application, created) => {
    setExisting(application);
    setEditing(false);
    setJustSubmitted(created);
  };

  if (initializing || loading) return null;

  if (preview) {
    const synthetic = { access_expires_at: new Date(Date.now() + 86400000).toISOString() };
    if (previewState === "pending") return <PageFrame preview><StatePage testid="mo-testing-already-submitted-state" title="Заявка уже отправлена" status="Заявка на рассмотрении" actions={<Button secondary onClick={() => nav("/admin?tab=mo_testing")}>← Вернуться в панель управления</Button>}><p>Заявка сохранена. После ручного рассмотрения здесь появится решение.</p></StatePage></PageFrame>;
    if (previewState === "approved_not_started") return <PageFrame preview><AcceptedState preview /></PageFrame>;
    if (previewState === "active") return <PageFrame preview><ActiveState application={synthetic} preview /></PageFrame>;
    if (previewState === "completed") return <PageFrame preview><CompletedState preview /></PageFrame>;
    if (previewState === "feedback_received") return <PageFrame preview><CompletedState feedbackReceived preview /></PageFrame>;
  }

  if (existing && !editing) {
    if (existing.status === "accepted") return <PageFrame><AcceptedState /></PageFrame>;
    if (existing.status === "active_test") return <PageFrame><ActiveState application={existing} /></PageFrame>;
    if (existing.status === "completed_test") return <PageFrame><CompletedState /></PageFrame>;
    if (existing.status === "feedback_received") return <PageFrame><CompletedState feedbackReceived /></PageFrame>;
    if (existing.status === "declined") return (
      <PageFrame><StatePage testid="mo-testing-declined-state" title="Заявка не одобрена" status="Решение принято"
        actions={<Button onClick={() => nav("/hall")}>Вернуться в Anokina Studio</Button>}>
        <p>Спасибо за интерес к Мастеру заказа. Повторная заявка для этого аккаунта недоступна.</p>
      </StatePage></PageFrame>
    );
    return (
      <PageFrame><StatePage testid={justSubmitted ? "mo-testing-just-submitted-state" : "mo-testing-already-submitted-state"}
        title={justSubmitted ? "Заявка отправлена" : "Заявка уже отправлена"} status="Заявка на рассмотрении"
        actions={<>
          <Button onClick={() => setEditing(true)}>Изменить заявку</Button>
          <Button secondary onClick={() => nav("/hall")}>Вернуться в Anokina Studio</Button>
        </>}>
        <p>Заявка сохранена. После ручного рассмотрения здесь появится решение.</p>
      </StatePage></PageFrame>
    );
  }

  return (
    <PageFrame>
      <EyebrowTitle title={editing ? "Изменить заявку" : (content.copy?.application_title || "Заявка на 24-часовое знакомство")} />
      {!editing && <div className="max-w-[62ch] text-[15px] leading-[1.7]" style={{ color: "#3E2E20" }}>
        <p className="whitespace-pre-line">{content.copy?.application_intro || "Познакомься с Мастером заказа и попробуй его в своей работе.\n\nПосле одобрения заявки ты сможешь начать знакомство тогда, когда тебе будет удобно."}</p>
        <p className="mt-3">{content.copy?.application_timer || "24 часа начнутся только после того, как ты нажмёшь кнопку «Начать»."}</p>
        <p className="mt-5 text-[14px]">{content.copy?.application_feedback_note || "После завершения знакомства мы попросим тебя поделиться впечатлениями. Твоя обратная связь поможет сделать Мастер заказа ещё понятнее и удобнее в реальной работе."}</p>
      </div>}
      {!user && !preview ? (
        <div className="mt-10"><Button onClick={() => {
          try { sessionStorage.setItem("post_login_redirect", "/master-order-testing"); } catch {}
          nav("/login");
        }}>Войти, чтобы подать заявку</Button></div>
      ) : (
        <ApplicationForm user={user} existing={editing ? existing : null} preview={preview} content={content.pre_trial || DEFAULT_PRE_CONTENT}
          onSaved={saved} onCancel={() => setEditing(false)} key={editing ? existing?.updated_at : "new"} />
      )}
    </PageFrame>
  );
}

function FieldLabel({ children }) {
  return <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>{children}</div>;
}
function ErrorText({ children }) {
  return <div className="mt-2 text-[12.5px]" style={{ color: "#8B3E2A" }} role="alert">{children}</div>;
}
function fieldStyle(selected = false) {
  return { background: selected ? "#F1E4CC" : "#FFFFFF", color: "#2A1D14", boxShadow: "inset 0 0 0 1px " + (selected ? "#C7A97A" : "#E4D3B0") };
}
function ReadOnlyField({ label, value, testid }) {
  return <div><FieldLabel>{label}</FieldLabel><div data-testid={testid} className="mt-2 h-11 px-4 flex items-center rounded-md text-[14px]" style={fieldStyle(true)}>{value}</div></div>;
}
function TextField({ label, value, onChange, error, testid, validationKey }) {
  return <div data-validation-key={validationKey}><FieldLabel>{label}</FieldLabel><input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="mt-2 w-full h-11 px-4 rounded-md text-[14px] outline-none" style={fieldStyle()} />{error && <ErrorText>{error}</ErrorText>}</div>;
}
function TextAreaField({ label, value, onChange, error, testid, validationKey }) {
  return <div data-validation-key={validationKey}><FieldLabel>{label}</FieldLabel><textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    rows={4} className="mt-2 w-full px-4 py-3 rounded-md text-[14px] outline-none resize-y" style={fieldStyle()} />{error && <ErrorText>{error}</ErrorText>}</div>;
}
function RadioField({ label, options, value, onChange, error, testid, validationKey }) {
  return <div data-testid={testid} data-validation-key={validationKey}><FieldLabel>{label}</FieldLabel><div className="mt-3 flex flex-col gap-2">{options.map((option) =>
    <label key={option.value} className="flex items-center gap-3 h-11 px-4 rounded-md text-[14px] cursor-pointer" style={fieldStyle(value === option.value)}>
      <input type="radio" name={testid} checked={value === option.value} onChange={() => onChange(option.value)} className="accent-[#4A3728]" />
      {option.label}
    </label>)}</div>{error && <ErrorText>{error}</ErrorText>}</div>;
}
function CheckboxField({ label, options, value, onToggle, error, testid, validationKey }) {
  return <div data-testid={testid} data-validation-key={validationKey}><FieldLabel>{label}</FieldLabel><div className="mt-3 flex flex-col gap-2">{options.map((option) =>
    <label key={option.value} className="flex items-center gap-3 min-h-11 px-4 py-2 rounded-md text-[14px] cursor-pointer" style={fieldStyle(value.includes(option.value))}>
      <input type="checkbox" checked={value.includes(option.value)} onChange={() => onToggle(option.value)} className="accent-[#4A3728]" />
      {option.label}
    </label>)}</div>{error && <ErrorText>{error}</ErrorText>}</div>;
}
